import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useClient } from '@/contexts/ClientContext';
import { Building2, CheckCircle2, XCircle } from 'lucide-react';
import { DashboardSkeleton } from '@/components/DashboardSkeleton';

interface DashboardClient {
  client_id: string;
  client_name: string;
  client_email: string;
  is_active?: boolean;
  permissions: {
    can_manage_connectors: boolean;
    can_manage_users: boolean;
    can_manage_billing: boolean;
  };
}

export function PartnerDashboard() {
  const navigate = useNavigate();
  const { setClients } = useClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['managed-clients'],
    queryFn: () => api.getManagedClients(),
  });

  useEffect(() => {
    if (data?.clients) {
      setClients(data.clients);
    }
  }, [data, setClients]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">Error loading clients: {(error as Error).message}</div>
      </div>
    );
  }

  const clients = data?.clients || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">My Clients</h1>
          <p className="mt-1 text-sm text-slate-400">
            Manage {clients.length} client{clients.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Empty State */}
      {clients.length === 0 && (
        <div className="text-center py-12 px-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <Building2 className="mx-auto h-12 w-12 text-slate-600" />
          <h3 className="mt-2 text-sm font-semibold text-slate-300">No clients assigned</h3>
          <p className="mt-1 text-sm text-slate-500">
            Your managed clients will appear here once assigned by an administrator
          </p>
        </div>
      )}

      {/* Client Grid */}
      {clients.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {clients.map((client: DashboardClient) => (
            <div
              key={client.client_id}
              className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6 hover:border-blue-500/50 transition-colors cursor-pointer"
            >
              {/* Client Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-slate-100 truncate">
                      {client.client_name}
                    </h3>
                  </div>
                  <p className="mt-1 text-sm text-slate-400 truncate">{client.client_email}</p>
                </div>
                <div>
                  {client.is_active ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-slate-600" />
                  )}
                </div>
              </div>

              {/* Permissions */}
              <div className="mt-4 space-y-2">
                <div className="text-xs text-slate-500 font-medium">PERMISSIONS</div>
                <div className="flex flex-wrap gap-2">
                  {client.permissions.can_manage_connectors && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      Connectors
                    </span>
                  )}
                  {client.permissions.can_manage_users && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      Users
                    </span>
                  )}
                  {client.permissions.can_manage_billing && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
                      Billing
                    </span>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <button
                  onClick={() => navigate(`/partner/clients/${client.client_id}`)}
                  className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  Manage Client →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
